import { DialogProps } from '@material-ui/core';
import { Form, Formik, FormikHelpers } from 'formik';
import { DialogTitleWithFormStepper } from 'framework/dialogs/DialogTitleWithFormStepper';
import { OverflowDialog } from 'framework/dialogs/OverflowDialog';
import { OverflowDialogContent } from 'framework/dialogs/OverflowDialogContent';
import { FormAutocompleteFreeSoloTextField } from 'framework/forms/FormAutocompleteFreeSoloTextField';
import { FormMoneyField } from 'framework/forms/FormMoneyField';
import { FormTextField } from 'framework/forms/FormTextField';
import { PageableFormDialogActions } from 'framework/forms/PageableFormDialogActions';
import { handleFormResponse } from 'framework/forms/utils/handleFormResponse';
import { useCacheContext } from 'framework/hooks/useCacheContext';
import { useFormSubmit } from 'framework/hooks/useFormSubmit';
import { useSnackbarNotify } from 'framework/hooks/useSnackbarNotify';
import { ICreateDiscountArticleRequest, productCatalogCommand_newDistcountArticle } from 'gen/ApiClient';
import { IStrings } from 'localization/IStrings';
import { useLocalization } from 'localization/useLocalization';
import React, { useState } from 'react';
import { FormSelectVatPercentageField } from 'shared/forms/FormSelectVatPercentageField';
import * as yup from 'yup';
import { ProductCatalogFilterDataContext } from '../context/ProductCatalogFilterDataContext';

const createSchema = (strings: IStrings) => {
	return yup
		.object<ICreateDiscountArticleRequest>({
			name: yup.string().required(strings.formRequired(strings.name)),
			value: yup.number().required(strings.formRequired(strings.priceInVat)),
			vatPercentage: yup.number().required(strings.formRequired(strings.vatPercentage)).defined(),
			category: yup.string(),
			subcategory: yup.string(),
		})
		.defined();
};

const emptyValues: ICreateDiscountArticleRequest = {
	name: '',
	category: '',
	subcategory: '',
	value: 0,
	vatPercentage: 0,
};

interface IProps extends DialogProps {
	confirm: (id: string) => void;
	cancel: VoidFunction;
	previous?: VoidFunction;
}

const stepsRecord: Record<number, (keyof ICreateDiscountArticleRequest)[]> = {
	0: ['name'],
	1: ['value', 'vatPercentage'],
	2: ['category', 'subcategory'],
};

export const CreateDiscountArticleForm = ({ confirm, cancel, previous, ...rest }: IProps) => {
	const strings = useLocalization();
	const notify = useSnackbarNotify();
	const [data] = useCacheContext(ProductCatalogFilterDataContext);
	const [create, isSubmitting] = useFormSubmit(productCatalogCommand_newDistcountArticle);
	const [step, setStep] = useState<number>(0);

	const handleSubmit = async (values: ICreateDiscountArticleRequest, helpers: FormikHelpers<ICreateDiscountArticleRequest>) => {
		const r = await create(values);
		if (handleFormResponse(r, helpers, stepsRecord, setStep)) {
			// navigate to detail?
			notify(strings.productCreated);
			confirm(r.result.objectId);
		}
	};

	if (data === undefined) {
		return <div></div>;
	}

	return (
		<Formik<ICreateDiscountArticleRequest>
			validateOnMount
			initialValues={emptyValues}
			validationSchema={createSchema(strings)}
			onSubmit={handleSubmit}>
			<Form>
				<OverflowDialog {...rest}>
					<DialogTitleWithFormStepper
						title={strings.newDiscountArticle}
						step={step}
						labels={[strings.name, strings.price, strings.category]}
					/>
					<OverflowDialogContent>
						{step === 0 && (
							<>
								<FormTextField<ICreateDiscountArticleRequest>
									name='name'
									label={strings.name}
									required
								/>
							</>
						)}
						{step === 1 && (
							<>
								<FormSelectVatPercentageField<ICreateDiscountArticleRequest>
									name='vatPercentage'
									label={strings.vatPercentage}
									required
								/>
								<FormMoneyField<ICreateDiscountArticleRequest>
									name='value'
									label={strings.priceInVat}
									allowNegative
								/>
							</>
						)}
						{step === 2 && (
							<>
								<FormAutocompleteFreeSoloTextField<ICreateDiscountArticleRequest>
									name='category'
									label={strings.category}
									options={data.categories}
								/>
								<FormAutocompleteFreeSoloTextField<ICreateDiscountArticleRequest>
									name='subcategory'
									label={strings.subcategory}
									options={data.subcategories}
								/>
							</>
						)}
					</OverflowDialogContent>
					<PageableFormDialogActions
						step={step}
						setStep={setStep}
						cancel={cancel}
						isSubmitting={isSubmitting}
						submitText={strings.create}
						schema={createSchema(strings)}
						stepsRecord={stepsRecord}
						previous={previous}
					/>
				</OverflowDialog>
			</Form>
		</Formik>
	);
};
